<template>
  <div class="create-settlement-container">
    <!-- 顶部标题栏 -->
    <div class="page-header">
      <div class="header-title">
        <div class="title-mark"></div>
        <span>发起结算申请</span>
      </div>
      <div class="header-actions">
        <el-button @click="goBack" icon="el-icon-arrow-left">返回</el-button>
      </div>
    </div>

    <!-- 步骤条 -->
    <div class="steps-container">
      <el-steps :active="currentStep" finish-status="success" align-center>
        <el-step title="选择订单" description="选择需要向需求方申请结算的订单"></el-step>
        <el-step title="选择发货记录" description="选择已签收的发货记录"></el-step>
        <el-step title="选择产品" description="选择申请结算的产品和数量"></el-step>
        <el-step title="确认申请" description="确认结算申请信息并提交给需求方"></el-step>
      </el-steps>
    </div>

    <!-- 步骤1: 选择订单 -->
    <div v-if="currentStep === 0" class="step-content">
      <div class="section-title">
        <h3>选择结算订单</h3>
        <p>请选择需要向需求方申请结算的订单，可以多选</p>
      </div>

      <!-- 订单搜索 -->
      <div class="search-area">
        <el-input
          v-model="orderSearchParams.order_title"
          placeholder="订单标题"
          clearable
          class="search-input"
          prefix-icon="el-icon-search"
        ></el-input>
        <el-input
          v-model="orderSearchParams.order_id"
          placeholder="订单编号"
          clearable
          class="search-input"
          prefix-icon="el-icon-search"
        ></el-input>
        <el-button type="primary" @click="searchOrders" icon="el-icon-search">搜索</el-button>
        <el-button @click="resetOrderSearch">重置</el-button>
      </div>

      <!-- 订单列表 -->
      <el-table
        ref="orderTable"
        :data="orderList"
        border
        style="width: 100%"
        v-loading="orderLoading"
        @selection-change="handleOrderSelectionChange"
        :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
      >
        <el-table-column
          type="selection"
          width="55"
          :selectable="isOrderSelectable"
        ></el-table-column>
        
        <el-table-column
          label="订单编号"
          width="150"
          align="center"
        >
          <template slot-scope="scope">
            {{ scope.row.orderId || scope.row.order_id }}
          </template>
        </el-table-column>
        
        <el-table-column
          label="订单标题"
          min-width="200"
          align="center"
        >
          <template slot-scope="scope">
            {{ scope.row.orderTitle || scope.row.order_title }}
          </template>
        </el-table-column>
        
        <el-table-column
          label="订单金额"
          width="120"
          align="center"
        >
          <template slot-scope="scope">
            {{ formatAmount(scope.row.priceTotal || scope.row.price_total || scope.row.products_total || 0) }}
          </template>
        </el-table-column>
        
        <el-table-column
          label="创建时间"
          width="150"
          align="center"
        >
          <template slot-scope="scope">
            {{ formatDate(scope.row.createTime || scope.row.create_time) }}
          </template>
        </el-table-column>
        
        <el-table-column label="产品数量" width="100" align="center">
              <template slot-scope="scope">
                {{ calculateOrderProductsTotal(scope.row) }}
              </template>
            </el-table-column>
      </el-table>

      <!-- 已选订单统计 -->
      <div class="selected-orders-summary" v-if="selectedOrders.length > 0">
        <div class="summary-info">
          <span>已选择 {{ selectedOrders.length }} 个订单</span>
          <span>总金额: {{ formatAmount(selectedOrdersTotalAmount) }} 元</span>
        </div>
      </div>

      <!-- 步骤1操作按钮 -->
      <div class="step-actions">
        <el-button 
          type="primary" 
          @click="nextToShipmentSelection"
          :disabled="selectedOrders.length === 0"
        >
          下一步 ({{ selectedOrders.length }})
        </el-button>
      </div>
    </div>

    <!-- 步骤2: 选择发货记录 -->
    <div v-if="currentStep === 1" class="step-content">
      <div class="section-title">
        <h3>选择发货记录</h3>
        <p>请选择已签收的发货记录，可以多选</p>
      </div>

      <!-- 发货记录搜索 -->
      <div class="search-area">
        <el-input
          v-model="shipmentSearchParams.trackingNumber"
          placeholder="物流单号"
          clearable
          class="search-input"
          prefix-icon="el-icon-search"
        ></el-input>
        <el-input
          v-model="shipmentSearchParams.logisticsCompany"
          placeholder="物流公司"
          clearable
          class="search-input"
          prefix-icon="el-icon-search"
        ></el-input>
        <el-button type="primary" @click="searchShipments" icon="el-icon-search">搜索</el-button>
        <el-button @click="resetShipmentSearch">重置</el-button>
      </div>

      <!-- 发货记录卡片列表 -->
      <div class="shipment-cards-container" v-loading="shipmentLoading">
        <div 
          v-for="shipment in shipmentList" 
          :key="shipment.id"
          class="shipment-card"
          :class="{ 'selected': isShipmentSelected(shipment.id) }"
          @click="toggleShipmentSelection(shipment)"
        >
          <div class="card-header">
            <el-checkbox 
              :value="isShipmentSelected(shipment.id)"
              @change="toggleShipmentSelection(shipment)"
              @click.stop
            ></el-checkbox>
            <span class="tracking-number">{{ shipment.trackingNumber || shipment.tracking_number }}</span>
            <span class="logistics-company">{{ shipment.logisticsCompany || shipment.logistics_company }}</span>
          </div>
          
          <div class="card-content">
            <div class="info-row">
              <span class="label">订单编号:</span>
              <span class="value">{{ shipment.orderId || shipment.order_id || '--' }}</span>
            </div>
            <div class="info-row">
              <span class="label">发货时间:</span>
              <span class="value">{{ formatDate(shipment.shipTime || shipment.ship_time) }}</span>
            </div>
            <div class="info-row">
              <span class="label">签收时间:</span>
              <span class="value">{{ formatDate(shipment.receivedTime || shipment.received_time) }}</span>
            </div>
            <div class="info-row">
              <span class="label">签收人:</span>
              <span class="value">{{ shipment.receivedBy || shipment.received_by || '--' }}</span>
            </div>
            <div class="info-row">
              <span class="label">发货数量:</span>
              <span class="value">{{ shipment.shipmentQuantity || 0 }}</span>
            </div>
          </div>
        </div>
        
        <!-- 全选/取消全选 -->
        <div class="select-all-container" v-if="shipmentList.length > 0">
          <el-button 
            @click="toggleSelectAllShipments"
            :type="allShipmentsSelected ? 'success' : 'primary'"
            size="small"
          >
            {{ allShipmentsSelected ? '取消全选' : '全选' }}
          </el-button>
        </div>
      </div>

      <!-- 已选发货记录统计 -->
      <div class="selected-shipments-summary" v-if="selectedShipments.length > 0">
        <div class="summary-info">
          <span>已选择 {{ selectedShipments.length }} 个发货记录</span>
        </div>
      </div>

      <!-- 步骤2操作按钮 -->
      <div class="step-actions">
        <el-button @click="prevStep">上一步</el-button>
        <el-button 
          type="primary" 
          @click="nextToProductSelection"
          :disabled="selectedShipments.length === 0"
        >
          下一步 ({{ selectedShipments.length }})
        </el-button>
      </div>
    </div>

    <!-- 步骤3: 选择产品 -->
    <div v-if="currentStep === 2" class="step-content">
      <div class="section-title">
        <h3>选择结算产品</h3>
        <p>请选择需要申请结算的产品，并设置结算数量</p>
      </div>

      <!-- 已选发货记录展示 -->
      <div class="selected-shipments-info">
        <div class="info-title">已选择的发货记录 ({{ selectedShipments.length }}个)</div>
        <div class="shipments-tags">
          <el-tag 
            v-for="shipment in selectedShipments" 
            :key="shipment.id"
            type="info"
            class="shipment-tag"
          >
            {{ shipment.trackingNumber || shipment.tracking_number }} - {{ shipment.logisticsCompany || shipment.logistics_company }}
          </el-tag>
        </div>
      </div>

      <!-- 产品列表 -->
      <div class="products-section">
        <div v-for="shipment in selectedShipments" :key="shipment.id" class="shipment-products">
          <div class="shipment-header">
            <h4>{{ shipment.trackingNumber || shipment.tracking_number }} - {{ shipment.logisticsCompany || shipment.logistics_company }}</h4>
            <div class="shipment-actions">
              <el-button 
                size="mini" 
                @click="toggleShipmentProducts(shipment.id)"
                :type="allShipmentProductsSelected(shipment.id) ? 'success' : 'primary'"
              >
                {{ allShipmentProductsSelected(shipment.id) ? '取消全选' : '全选' }}
              </el-button>
            </div>
          </div>

          <el-table
            :data="shipment.products || []"
            border
            style="width: 100%"
            :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
          >
            <el-table-column width="55" align="center">
              <template slot-scope="scope">
                <el-checkbox 
                  v-model="scope.row.selected"
                  @change="handleProductSelectionChange(shipment.id, scope.row)"
                ></el-checkbox>
              </template>
            </el-table-column>
            
            <el-table-column
              label="材料名称"
              min-width="150"
              align="center"
            >
              <template slot-scope="scope">
                {{ scope.row.productName || scope.row.product_name || '--' }}
              </template>
            </el-table-column>
            
            <el-table-column
              label="规格型号"
              width="120"
              align="center"
            >
              <template slot-scope="scope">
            <el-tooltip
              v-if="getSpecificationText(scope.row)"
              :content="getSpecificationText(scope.row)"
              placement="top"
              effect="dark"
              popper-class="spec-tooltip"
            >
              <span>{{ truncateText(getSpecificationText(scope.row), 20) }}</span>
            </el-tooltip>
            <span v-else>--</span>
              </template>
            </el-table-column>
            
            <el-table-column
              label="材质要求"
              width="120"
              align="center"
            >
              <template slot-scope="scope">
                {{ scope.row.materialRequirement || scope.row.material_requirement || '--' }}
              </template>
            </el-table-column>
            
            <el-table-column
              label="发货数量"
              width="120"
              align="center"
            >
              <template slot-scope="scope">
                {{ scope.row.shippedQuantity || scope.row.shipped_quantity || 0 }} {{ scope.row.unit || '个' }}
              </template>
            </el-table-column>
            
            <el-table-column
              label="合同价"
              width="100"
              align="center"
            >
              <template slot-scope="scope">
                {{ formatAmount(scope.row.taxInclusiveUnitPrice || scope.row.tax_inclusive_unit_price || 0) }}
              </template>
            </el-table-column>
            
            <el-table-column
              label="结算数量"
              width="150"
              align="center"
            >
              <template slot-scope="scope">
                <el-input-number
                  v-model="scope.row.settlement_quantity"
                  :min="0"
                  :max="Number(scope.row.shippedQuantity || scope.row.shipped_quantity)"
                  :precision="2"
                  :disabled="!scope.row.selected"
                  size="mini"
                  @change="handleQuantityChange(shipment.id, scope.row)"
                ></el-input-number>
              </template>
            </el-table-column>
            
            <el-table-column
              label="税率(%)"
              min-width="100"
              align="center"
            >
              <template slot-scope="scope">
                <el-input-number
                  v-model="scope.row.taxRate"
                  :min="0"
                  :max="100"
                  :precision="2"
                  :disabled="!scope.row.selected"
                  size="mini"
                  placeholder="13.00"
                ></el-input-number>
              </template>
            </el-table-column>
            
            <el-table-column
              label="结算金额"
              width="120"
              align="center"
            >
              <template slot-scope="scope">
                {{ formatAmount(calculateProductSettlementAmount(scope.row)) }}
              </template>
            </el-table-column>
            
            <el-table-column
              label="含税金额"
              width="120"
              align="center"
            >
              <template slot-scope="scope">
                {{ formatAmount(calculateProductTaxAmount(scope.row)) }}
              </template>
            </el-table-column>
            

          </el-table>
        </div>
      </div>

      <!-- 结算汇总 -->
      <div class="settlement-summary">
        <div class="summary-card">
          <div class="summary-title">结算汇总</div>
          <div class="summary-content">
            <div class="summary-item">
              <span>选择产品数量:</span>
              <span>{{ selectedProductsCount }} 项</span>
            </div>
            <div class="summary-item">
              <span>结算总金额(不含税):</span>
              <span class="amount">{{ formatAmount(settlementTotalAmount) }} 元</span>
            </div>
            <div class="summary-item">
              <span>结算总金额(含税):</span>
              <span class="amount highlight">{{ formatAmount(settlementTotalTaxAmount) }} 元</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 步骤2操作按钮 -->
      <div class="step-actions">
        <el-button @click="prevStep">上一步</el-button>
        <el-button 
          type="primary" 
          @click="nextToConfirm"
          :disabled="selectedProductsCount === 0"
        >
          下一步
        </el-button>
      </div>
    </div>

    <!-- 步骤4: 确认结算 -->
    <div v-if="currentStep === 3" class="step-content">
      <div class="section-title">
        <h3>确认结算申请信息</h3>
        <p>请确认向需求方提交的结算申请信息并填写必要的信息</p>
      </div>

      <!-- 结算基本信息 -->
      <el-form :model="settlementForm" :rules="settlementFormRules" ref="settlementForm" label-width="120px">
        <el-form-item label="结算标题" prop="settlement_title">
          <el-input v-model="settlementForm.settlement_title" placeholder="请输入结算标题"></el-input>
        </el-form-item>
        
        <el-form-item label="结算时间" prop="settlement_date">
          <el-date-picker
            v-model="settlementForm.settlement_date"
            type="datetime"
            placeholder="请选择结算时间"
            format="yyyy-MM-dd HH:mm:ss"
            value-format="yyyy-MM-ddTHH:mm:ss"
            style="width: 100%;"
          ></el-date-picker>
        </el-form-item>
        
        <el-form-item label="备注">
          <el-input 
            v-model="settlementForm.description"
            type="textarea"
            :rows="3"
            placeholder="请输入备注信息（可选）"
          ></el-input>
        </el-form-item>
      </el-form>

      <!-- 结算详情确认 -->
      <div class="confirm-details">
        <div class="details-title">结算详情确认</div>
        
        <!-- 发货记录汇总 -->
        <div class="shipments-summary">
          <h4>涉及发货记录 ({{ selectedShipments.length }}个)</h4>
          <el-table :data="selectedShipments" border size="small">
            <el-table-column label="物流单号" align="center">
              <template slot-scope="scope">
                {{ scope.row.trackingNumber || scope.row.tracking_number || '--' }}
              </template>
            </el-table-column>
            <el-table-column label="物流公司" align="center">
              <template slot-scope="scope">
                {{ scope.row.logisticsCompany || scope.row.logistics_company || '--' }}
              </template>
            </el-table-column>
            <el-table-column label="订单编号" align="center">
              <template slot-scope="scope">
                {{ scope.row.orderNumber || scope.row.order_number || '--' }}
              </template>
            </el-table-column>
            <el-table-column label="发货时间" align="center">
              <template slot-scope="scope">
                {{ formatDate(scope.row.shipTime || scope.row.ship_time) }}
              </template>
            </el-table-column>
            <el-table-column label="签收时间" align="center">
              <template slot-scope="scope">
                {{ formatDate(scope.row.receivedTime || scope.row.received_time) }}
              </template>
            </el-table-column>
            <el-table-column label="结算产品数" align="center">
              <template slot-scope="scope">
                {{ getShipmentSelectedProductsCount(scope.row.id) }}
              </template>
            </el-table-column>
            <el-table-column label="结算金额(不含税)" align="center">
              <template slot-scope="scope">
                {{ formatAmount(getShipmentSettlementAmount(scope.row.id)) }}
              </template>
            </el-table-column>
            <el-table-column label="结算金额(含税)" align="center">
              <template slot-scope="scope">
                {{ formatAmount(getShipmentSettlementTaxAmount(scope.row.id)) }}
              </template>
            </el-table-column>
          </el-table>
        </div>
        
        <!-- 产品明细 -->
        <div class="products-detail">
          <h4>产品明细</h4>
          <el-table :data="getSelectedProductsList()" border size="small">
            <el-table-column prop="logistics_id" label="物流单号" align="center"></el-table-column>
            <el-table-column label="材料名称" align="center">
              <template slot-scope="scope">
                {{ scope.row.productName || scope.row.product_name || '--' }}
              </template>
            </el-table-column>
            <el-table-column label="规格型号" align="center">
              <template slot-scope="scope">
                <el-tooltip
                  v-if="getSpecificationText(scope.row)"
                  :content="getSpecificationText(scope.row)"
                  placement="top"
                  effect="dark"
                  popper-class="spec-tooltip"
                >
                  <span>{{ truncateText(getSpecificationText(scope.row), 20) }}</span>
                </el-tooltip>
                <span v-else>--</span>
              </template>
            </el-table-column>
            <el-table-column label="材质要求" align="center">
              <template slot-scope="scope">
                {{ scope.row.materialRequirement || scope.row.material_requirement || '-' }}
              </template>
            </el-table-column>
            <el-table-column label="发货数量" align="center">
              <template slot-scope="scope">
                {{ scope.row.shippedQuantity || scope.row.shipped_quantity || 0 }} {{ scope.row.unit || '个' }}
              </template>
            </el-table-column>
            <el-table-column label="结算数量" align="center">
              <template slot-scope="scope">
                {{ scope.row.settlement_quantity }} {{ scope.row.unit || '个' }}
              </template>
            </el-table-column>
            <el-table-column label="合同价" align="center">
              <template slot-scope="scope">
                {{ formatAmount(scope.row.taxInclusiveUnitPrice || scope.row.tax_inclusive_unit_price || 0) }}
              </template>
            </el-table-column>
            <el-table-column label="税率" align="center">
              <template slot-scope="scope">
                {{ scope.row.taxRate || 0 }}%
              </template>
            </el-table-column>
            <el-table-column label="不含税金额" align="center">
              <template slot-scope="scope">
                {{ formatAmount(calculateProductSettlementAmount(scope.row)) }}
              </template>
            </el-table-column>
            <el-table-column label="含税金额" align="center">
              <template slot-scope="scope">
                {{ formatAmount(calculateProductTaxAmount(scope.row)) }}
              </template>
            </el-table-column>

          </el-table>
        </div>

        <!-- 总计信息 -->
        <div class="total-info">
          <div class="total-item">
            <span>结算产品总数:</span>
            <span>{{ selectedProductsCount }} 项</span>
          </div>
          <div class="total-item">
            <span>结算总金额(不含税):</span>
            <span>{{ formatAmount(settlementTotalAmount) }} 元</span>
          </div>
          <div class="total-item">
            <span>税额合计:</span>
            <span>{{ formatAmount(settlementTotalTaxAmount - settlementTotalAmount) }} 元</span>
          </div>
          <div class="total-item highlight">
            <span>结算总金额(含税):</span>
            <span>{{ formatAmount(settlementTotalTaxAmount) }} 元</span>
          </div>
        </div>
      </div>

      <!-- 步骤3操作按钮 -->
      <div class="step-actions">
        <el-button @click="prevStep">上一步</el-button>
        <el-button type="primary" @click="submitSettlement" :loading="submitLoading">提交结算申请</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { create_settlement, get_settleable_orders, get_all_settlements } from '@/api/modules/settlement';
import { query_logistics, get_shipment_list_by_logistics_id } from '@/api/modules/logistics';

export default {
  name: 'CreateSettlement',
  data() {
    return {
      // 当前步骤
      currentStep: 0,
      
      // 订单相关
      orderList: [],
      orderLoading: false,
      selectedOrders: [],
      orderSearchParams: {
        order_title: '',
        order_id: ''
      },
      
      // 发货记录相关
      shipmentList: [],
      shipmentLoading: false,
      selectedShipments: [],
      shipmentSearchParams: {
        trackingNumber: '',
        logisticsCompany: ''
      },
      
      // 结算表单
      settlementForm: {
        settlement_title: '',
        settlement_date: '', // 结算时间
        description: ''
      },
      
      // 表单验证规则
      settlementFormRules: {
        settlement_title: [
          { required: true, message: '请输入结算标题', trigger: 'blur' },
          { min: 2, max: 50, message: '标题长度在 2 到 50 个字符', trigger: 'blur' }
        ],
        settlement_date: [
          { required: true, message: '请选择结算时间', trigger: 'change' }
        ]
      },
      
      // 提交状态
      submitLoading: false,
      
      // 当前用户单位ID
      currentUnitId: null
    };
  },
  
  computed: {
    // 已选订单总金额
    selectedOrdersTotalAmount() {
      return this.selectedOrders.reduce((total, order) => {
        return total + (Number(order.priceTotal) || Number(order.price_total) || Number(order.products_total) || 0);
      }, 0);
    },
    
    // 已选产品数量
    selectedProductsCount() {
      let count = 0;
      this.selectedShipments.forEach(shipment => {
        const products = shipment.products;
        if (products) {
          products.forEach(item => {
            if (item.selected && item.settlement_quantity > 0) {
              count++;
            }
          });
        }
      });
      return count;
    },
    
    // 结算总金额（不含税）
    settlementTotalAmount() {
      let total = 0;
      this.selectedShipments.forEach(shipment => {
        const products = shipment.products;
        if (products) {
          products.forEach(item => {
            if (item.selected) {
              total += this.calculateProductSettlementAmount(item);
            }
          });
        }
      });
      return total;
    },
    
    // 结算总金额（含税）
    settlementTotalTaxAmount() {
      let total = 0;
      this.selectedShipments.forEach(shipment => {
        const products = shipment.products;
        if (products) {
          products.forEach(item => {
            if (item.selected) {
              total += this.calculateProductTaxAmount(item);
            }
          });
        }
      });
      return total;
    },
    
    // 是否全选发货记录
    allShipmentsSelected() {
      return this.shipmentList.length > 0 && this.selectedShipments.length === this.shipmentList.length;
    }
  },
  
  created() {
    this.getCurrentUnitId();
    this.getOrderList();
  },
  
  methods: {
    // 获取规格型号文本
    getSpecificationText(item) {
      if (!item) return '';
      return (
        item.specification ||
        item.spec ||
        item.materialsSpec ||
        item.itemSpecification ||
        ''
      );
    },

    // 截断文本显示，默认为20个字符
    truncateText(text, length = 20) {
      if (!text) return '-';
      const str = String(text);
      return str.length > length ? `${str.slice(0, length)}...` : str;
    },

    // 获取当前用户的单位ID
    getCurrentUnitId() {
      try {
        const store = this.$store || {};
        const state = store.state || {};
        
        let userInfo = null;
        
        if (state.user && state.user.userInfo) {
          userInfo = state.user.userInfo;
        } else if (state.userInfo) {
          userInfo = state.userInfo;
        } else if (state.auth && state.auth.userInfo) {
          userInfo = state.auth.userInfo;
        }
        
        if (!userInfo || !userInfo.unitId) {
          const localUserInfo = localStorage.getItem('userInfo');
          if (localUserInfo) {
            try {
              userInfo = JSON.parse(localUserInfo);
            } catch (e) {
              console.error('解析localStorage中的userInfo失败:', e);
            }
          }
        }
        
        if (userInfo && userInfo.unitId) {
          this.currentUnitId = userInfo.unitId;
        } else {
          this.currentUnitId = 1; // 默认值
        }
      } catch (err) {
        console.error('获取用户unitId出错:', err);
        this.currentUnitId = 1;
      }
    },
    
    // 获取发货记录列表
    getShipmentList() {
      this.shipmentLoading = true;
      
      // 获取选中订单的物流信息
      const orderIds = this.selectedOrders.map(order => order.id || order.orderId);
      
      if (orderIds.length === 0) {
        this.shipmentList = [];
        this.shipmentLoading = false;
        return;
      }
      
      // 查询第一个订单的物流信息（简化处理）
      query_logistics({
        orderId: orderIds[0],
        page: 0,
        size: 100
      })
        .then(response => {
          let allShipments = [];
          
          if (response.data && response.data.success) {
            const data = response.data.data;
            
            if (data && data.content && Array.isArray(data.content)) {
              allShipments = data.content;
            } else if (Array.isArray(data)) {
              allShipments = data;
            }
          }
          
          // 过滤出已签收的物流信息
          let filteredShipments = allShipments.filter(shipment => 
            shipment.isReceived === true || 
            shipment.logisticsStatus === '4' ||
            shipment.logisticsStatus === 4 ||
            (shipment.receivedTime && shipment.receivedTime.trim() !== '')
          );
          
          // 如果有搜索条件，进一步进行客户端过滤
          if (this.shipmentSearchParams.trackingNumber || this.shipmentSearchParams.logisticsCompany) {
            filteredShipments = filteredShipments.filter(shipment => {
              let matchTracking = true;
              let matchCompany = true;
              
              if (this.shipmentSearchParams.trackingNumber) {
                matchTracking = (shipment.trackingNumber || shipment.tracking_number || '').toLowerCase()
                  .includes(this.shipmentSearchParams.trackingNumber.toLowerCase());
              }
              
              if (this.shipmentSearchParams.logisticsCompany) {
                matchCompany = (shipment.logisticsCompany || shipment.logistics_company || '').toLowerCase()
                  .includes(this.shipmentSearchParams.logisticsCompany.toLowerCase());
              }
              
              return matchTracking && matchCompany;
            });
          }
          
          this.shipmentList = filteredShipments;
          this.shipmentLoading = false;
        })
        .catch(error => {
          console.error('获取发货记录列表失败:', error);
          this.$message.error('获取发货记录列表失败，请稍后重试');
          this.shipmentList = [];
          this.shipmentLoading = false;
        });
    },
    
    // 获取发货记录的产品清单
    getShipmentProducts() {
      if (this.selectedShipments.length === 0) {
        return;
      }
      
      // 为每个选中的发货记录获取产品清单
      const promises = this.selectedShipments.map(shipment => {
        return get_shipment_list_by_logistics_id(shipment.id || shipment.logisticsInfoId)
          .then(response => {
            if (response.data && response.data.success) {
              const products = response.data.data || [];
              return {
                ...shipment,
                products: products.map(product => ({
                  ...product,
                  selected: false,
                  settlement_quantity: Number(product.shippedQuantity) || 0,
                  taxRate: product.taxRate || product.tax_rate || 13.00
                }))
              };
            }
            return { ...shipment, products: [] };
          })
          .catch(error => {
            console.error(`获取发货记录${shipment.id}的产品清单失败:`, error);
            return { ...shipment, products: [] };
          });
      });
      
      Promise.all(promises)
        .then(shipmentsWithProducts => {
          this.selectedShipments = shipmentsWithProducts;
        });
    },
    
    
    
    // 判断发货记录是否已选中
    isShipmentSelected(shipmentId) {
      return this.selectedShipments.some(item => item.id === shipmentId);
    },
    
    // 切换发货记录选择状态
    toggleShipmentSelection(shipment) {
      const index = this.selectedShipments.findIndex(item => item.id === shipment.id);
      if (index > -1) {
        // 已选中，取消选择
        this.selectedShipments.splice(index, 1);
      } else {
        // 未选中，添加到选择列表
        this.selectedShipments.push(shipment);
      }
    },
    
    // 全选/取消全选发货记录
    toggleSelectAllShipments() {
      if (this.allShipmentsSelected) {
        // 当前是全选状态，取消全选
        this.selectedShipments = [];
      } else {
        // 当前不是全选状态，全选
        this.selectedShipments = [...this.shipmentList];
      }
    },
    
    
    // 搜索发货记录
    searchShipments() {
      this.getShipmentList();
    },
    
    // 重置发货记录搜索
    resetShipmentSearch() {
      this.shipmentSearchParams = {
        trackingNumber: '',
        logisticsCompany: ''
      };
      this.getShipmentList();
    },

    // 获取订单列表
    getOrderList() {
      this.orderLoading = true;
      
      // 使用专门的可结算订单接口
      get_settleable_orders()
        .then(response => {
          let allOrders = [];
          
          if (response.data && response.data.success) {
            const data = response.data.data;
            
            if (Array.isArray(data)) {
              allOrders = data;
            }
          }
          
          // 首先过滤出当前用户单位作为供应商的订单
          const supplierOrders = allOrders.filter(order => this.isSupplierOrder(order));
          
          // 如果有搜索条件，进一步进行客户端过滤
          if (this.orderSearchParams.order_title || this.orderSearchParams.order_id) {
            this.orderList = supplierOrders.filter(order => {
              let matchTitle = true;
              let matchId = true;
              
              if (this.orderSearchParams.order_title) {
                 matchTitle = (order.orderTitle || order.order_title || '').toLowerCase().includes(this.orderSearchParams.order_title.toLowerCase());
               }
               
               if (this.orderSearchParams.order_id) {
                 matchId = (order.orderId || order.order_id || '').toLowerCase().includes(this.orderSearchParams.order_id.toLowerCase());
               }
              
              return matchTitle && matchId;
            });
          } else {
            this.orderList = supplierOrders;
          }
          
          // 如果过滤后没有订单，提示用户
          if (supplierOrders.length === 0 && allOrders.length > 0) {
this.$message.info('暂无可结算的订单。只能对当前单位作为供应商的订单发起结算申请。');
          }
          
          // 初始化订单中的产品选择状态
          this.orderList.forEach(order => {
            const orderItems = order.orderItems || order.OrderItems;
            if (orderItems && Array.isArray(orderItems)) {
              orderItems.forEach(item => {
                 this.$set(item, 'selected', false);
                 this.$set(item, 'settlement_quantity', Number(item.shippedQuantity) || 0);
                 // 兼容新旧字段名
                 this.$set(item, 'taxRate', item.taxRate || item.product_tax_rate || 13.00);
                 this.$set(item, 'remark', item.remark || '');
                 // 确保新字段的兼容性
                 this.$set(item, 'productName', item.productName || item.materialsName || item.product_name || item.materials_name || item.name);
                 this.$set(item, 'specification', item.specification || item.materialsSpec || item.materials_spec || item.spec || '');
                 this.$set(item, 'materialRequirement', item.materialRequirement || '');
                 this.$set(item, 'taxInclusiveUnitPrice', item.taxInclusiveUnitPrice || item.price || item.product_tax_price || 0);
              });
              
              // 确保两种字段名都存在，保持兼容性
              if (!order.OrderItems) {
                this.$set(order, 'OrderItems', orderItems);
              }
              if (!order.orderItems) {
                this.$set(order, 'orderItems', orderItems);
              }
            }
          });
          
          this.orderLoading = false;
        })
        .catch(error => {
          console.error('获取可结算订单列表失败:', error);
          this.$message.error('获取可结算订单列表失败，请稍后重试');
          this.orderList = [];
          this.orderLoading = false;
        });
    },
    
    // 获取已结算订单ID列表
    getSettledOrderIds() {
      return new Promise((resolve) => {
        get_all_settlements()
          .then(response => {
            let settlements = [];
            
            if (response.data && response.data.success) {
              const data = response.data.data;
              if (Array.isArray(data)) {
                settlements = data;
              } else if (data && data.list && Array.isArray(data.list)) {
                settlements = data.list;
              }
            }
            
            const orderIds = settlements
              .filter(item => item && item.order_id !== undefined && item.order_id !== null)
              .map(item => Number(item.order_id))
              .filter(id => !isNaN(id));
            
            resolve([...new Set(orderIds)]);
          })
          .catch(error => {
            console.error('获取结算单出错:', error);
            resolve([]);
          });
      });
    },
    

    
    // 搜索订单
    searchOrders() {
      this.getOrderList();
    },
    
    // 重置搜索
    resetOrderSearch() {
      this.orderSearchParams = {
        order_title: '',
        order_id: ''
      };
      this.getOrderList();
    },
    
    // 判断订单是否是当前用户单位作为供应商的订单
    isSupplierOrder(order) {
      if (!this.currentUnitId || !order) {
        return false;
      }
      
      // 检查订单的供应商单位
      if (order.contractDetails && order.contractDetails.length > 0) {
        for (const contract of order.contractDetails) {
          if (contract.supplierUnits && contract.supplierUnits.length > 0) {
            for (const unit of contract.supplierUnits) {
              // 多种方式比较单位ID
              if (unit.unitId === this.currentUnitId || 
                  unit.unitId == this.currentUnitId || 
                  String(unit.unitId) === String(this.currentUnitId) ||
                  Number(unit.unitId) === Number(this.currentUnitId) ||
                  unit.id === this.currentUnitId ||
                  unit.id == this.currentUnitId) {
                return true;
              }
            }
          }
        }
      }
      
      // 兼容旧数据结构：检查供应商ID
      if (order.supplier_id === this.currentUnitId || 
          order.supplierId === this.currentUnitId) {
        return true;
      }
      
      return false;
    },
    
    // 判断订单是否可选
    isOrderSelectable(row) {
      // 订单必须有产品且当前用户单位是供应商
      const hasProducts = row && ((row.orderItems && row.orderItems.length > 0) || (row.OrderItems && row.OrderItems.length > 0));
      return hasProducts && this.isSupplierOrder(row);
    },
    
    // 处理订单选择变化
    handleOrderSelectionChange(selection) {
      this.selectedOrders = selection;
    },
    
    // 下一步：发货记录选择
    nextToShipmentSelection() {
      if (this.selectedOrders.length === 0) {
        this.$message.warning('请先选择订单');
        return;
      }
      
      // 获取选中订单的已签收发货记录
      this.getShipmentList();
      this.currentStep = 1;
    },
    
    // 下一步：产品选择
    nextToProductSelection() {
      if (this.selectedShipments.length === 0) {
        this.$message.warning('请先选择发货记录');
        return;
      }
      
      // 获取选中发货记录的产品清单
      this.getShipmentProducts();
      this.currentStep = 2;
    },
    
    // 上一步
    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
      }
    },
    
    // 切换发货记录产品全选
    toggleShipmentProducts(shipmentId) {
      const shipment = this.selectedShipments.find(s => s.id === shipmentId);
      const products = shipment?.products;
      if (!shipment || !products) return;
      
      const allSelected = this.allShipmentProductsSelected(shipmentId);
      
      products.forEach(item => {
        this.$set(item, 'selected', !allSelected);
        if (!allSelected) {
          this.$set(item, 'settlement_quantity', Number(item.shippedQuantity || item.shipped_quantity) || 0);
          // 确保税率存在
          if (!item.taxRate) {
            this.$set(item, 'taxRate', 13.00);
          }
        } else {
          this.$set(item, 'settlement_quantity', 0);
        }
      });
    },
    
    // 判断发货记录产品是否全选
    allShipmentProductsSelected(shipmentId) {
      const shipment = this.selectedShipments.find(s => s.id === shipmentId);
      const products = shipment?.products;
      if (!shipment || !products || products.length === 0) return false;
      
      return products.every(item => item.selected);
    },
    
    // 处理产品选择变化
    handleProductSelectionChange(shipmentId, product) {
      if (product.selected) {
        // 选中时设置默认结算数量为发货数量
        this.$set(product, 'settlement_quantity', Number(product.shippedQuantity || product.shipped_quantity) || 0);
        // 确保税率存在
        if (!product.taxRate) {
          this.$set(product, 'taxRate', 13.00);
        }
      } else {
        // 取消选中时清零结算数量
        this.$set(product, 'settlement_quantity', 0);
      }
    },
    
    // 处理数量变化
    handleQuantityChange(shipmentId, product) {
      // 确保数量在合理范围内
      const maxQuantity = Number(product.shippedQuantity || product.shipped_quantity);
      if (Number(product.settlement_quantity) > maxQuantity) {
        product.settlement_quantity = maxQuantity;
      }
      if (Number(product.settlement_quantity) < 0) {
        product.settlement_quantity = 0;
      }
    },
    
    // 计算产品结算金额
    calculateProductSettlementAmount(product) {
      if (!product.selected || !product.settlement_quantity) return 0;
      // 使用发货记录的字段名
      return (product.taxInclusiveUnitPrice || product.tax_inclusive_unit_price || 0) * (product.settlement_quantity || 0);
    },
    
    // 计算产品含税金额
    calculateProductTaxAmount(product) {
      if (!product.selected || !product.settlement_quantity) return 0;
      const settlementAmount = this.calculateProductSettlementAmount(product);
      const taxRate = product.taxRate || product.product_tax_rate || 13.00;
      return settlementAmount * (1 + taxRate / 100);
    },
    
    // 下一步：确认结算
    nextToConfirm() {
      if (this.selectedProductsCount === 0) {
        this.$message.warning('请先选择需要结算的产品');
        return;
      }
      
      // 生成默认的结算标题
      this.generateDefaultSettlementTitle();
      
      this.currentStep = 3;
    },
    
    // 生成默认结算标题
    generateDefaultSettlementTitle() {
      const now = new Date();
      const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
      
      if (this.selectedOrders.length === 1) {
        this.settlementForm.settlement_title = `${this.selectedOrders[0].orderTitle || this.selectedOrders[0].order_title || this.selectedOrders[0].orderId || this.selectedOrders[0].order_id}结算单_${dateStr}`;
      } else {
        this.settlementForm.settlement_title = `批量结算单_${dateStr}`;
      }
      
      // 设置默认结算时间为当前时间
      this.settlementForm.settlement_date = new Date().toISOString().slice(0, 19);
    },
    
    // 获取发货记录已选产品数量
    getShipmentSelectedProductsCount(shipmentId) {
      const shipment = this.selectedShipments.find(s => s.id === shipmentId);
      const products = shipment?.products;
      if (!shipment || !products) return 0;
      
      return products.filter(item => item.selected && item.settlement_quantity > 0).length;
    },
    
    // 获取发货记录结算金额（不含税）
    getShipmentSettlementAmount(shipmentId) {
      const shipment = this.selectedShipments.find(s => s.id === shipmentId);
      const products = shipment?.products;
      if (!shipment || !products) return 0;
      
      return products
        .filter(item => item.selected && item.settlement_quantity > 0)
        .reduce((total, item) => total + this.calculateProductSettlementAmount(item), 0);
    },
    
    // 获取发货记录结算金额（含税）
    getShipmentSettlementTaxAmount(shipmentId) {
      const shipment = this.selectedShipments.find(s => s.id === shipmentId);
      const products = shipment?.products;
      if (!shipment || !products) return 0;
      
      return products
        .filter(item => item.selected && item.settlement_quantity > 0)
        .reduce((total, item) => total + this.calculateProductTaxAmount(item), 0);
    },
    
    // 获取所有选中的产品列表
    getSelectedProductsList() {
      const products = [];
      this.selectedShipments.forEach(shipment => {
        const shipmentProducts = shipment.products;
        if (shipmentProducts) {
          shipmentProducts
            .filter(item => item.selected && item.settlement_quantity > 0)
            .forEach(item => {
              products.push({
                ...item,
                logistics_id: shipment.id,
                tracking_number: shipment.trackingNumber || shipment.tracking_number,
                logistics_company: shipment.logisticsCompany || shipment.logistics_company,
                order_id: shipment.orderId || shipment.order_id,
                order_number: shipment.orderNumber || shipment.order_number,
                // 确保字段名兼容
                productName: item.productName || item.product_name,
                specification: item.specification || item.spec,
                materialRequirement: item.materialRequirement || item.material_requirement || '',
                shippedQuantity: item.shippedQuantity || item.shipped_quantity || 0,
                product_name: item.productName || item.product_name,
                unit: item.unit,
                taxInclusiveUnitPrice: item.taxInclusiveUnitPrice || item.tax_inclusive_unit_price,
                taxRate: item.taxRate
              });
            });
        }
      });
      return products;
    },
    
    // 提交结算
    submitSettlement() {
      this.$refs.settlementForm.validate(valid => {
        if (!valid) {
          return false;
        }
        
        // 数据验证
        if (!this.validateSettlementData()) {
          return false;
        }
        
        this.submitLoading = true;
        
        // 构建产品明细列表
        const products = [];
        this.selectedShipments.forEach(shipment => {
          const shipmentProducts = shipment.products;
          if (shipmentProducts) {
            shipmentProducts
              .filter(item => item.selected && item.settlement_quantity > 0)
              .forEach(item => {
                const settlementAmount = this.calculateProductSettlementAmount(item);
                const taxRate = item.taxRate || 13.00; // 默认税率13%
                const taxPrice = settlementAmount * (1 + taxRate / 100);
                
                products.push({
                  logistics_info_id: shipment.id,
                  shipment_list_id: item.id, // 发货清单ID
                  product_name: item.productName || item.product_name || '',
                  specification: item.specification || item.spec || '',
                  material_requirement: item.materialRequirement || item.material_requirement || '',
                  original_quantity: Number(item.shippedQuantity || item.shipped_quantity || 0),
                  settlement_quantity: Number(item.settlement_quantity || 0),
                  product_unit: item.unit || '个',
                  product_price: Number(item.taxInclusiveUnitPrice || item.tax_inclusive_unit_price || 0),
                  price_total: Number(settlementAmount),
                  product_tax_rate: Number(taxRate),
                  product_tax_price: Number(taxPrice.toFixed(2)),
                  remark: item.remark || ''
                });
              });
          }
        });
        
        // 构建结算数据，按照新的API接口要求
        const settlementData = {
          // 不提供id字段，表示新增
          settlementNumber: `JS${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, '0')}${String(new Date().getDate()).padStart(2, '0')}${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`,
          settlementTitle: this.settlementForm.settlement_title,
          settlementDate: this.settlementForm.settlement_date,
          supplierConfirmationDate: null, // 供应商确认时间，创建时为空
          remark: this.settlementForm.description || '', // 备注信息
          amount: Number(this.settlementTotalAmount.toFixed(2)),
          demandUnitIds: this.getDemandUnitIds(), // 需求方单位ID列表
          demand: this.getDemandInfo(),
          logisticsInfoIds: this.selectedShipments.map(shipment => shipment.id),
          orderIds: this.getOrderIdsFromShipments(), // 从发货记录中提取订单ID
          settlementItems: this.buildSettlementItems()
        };
        
// 调用API创建结算单
        create_settlement(settlementData)
          .then(response => {
if (response.data && response.data.success) {
              this.$message.success('结算申请提交成功，已发送给需求方确认');
              this.$router.push('/settlement/settlements-list');
            } else {
              const errorMsg = response.data?.message || '提交结算申请失败';
              console.error('提交结算申请失败:', errorMsg);
              this.$message.error(errorMsg);
            }
            this.submitLoading = false;
          })
          .catch(error => {
            console.error('创建结算单请求失败:', error);
            
            // 尝试从错误响应中获取具体错误信息
            let errorMessage = '提交结算申请失败，请稍后重试';
            if (error.response && error.response.data) {
              if (error.response.data.message) {
                errorMessage = error.response.data.message;
              } else if (error.response.data.error) {
                errorMessage = error.response.data.error;
              }
            } else if (error.message) {
              errorMessage = error.message;
            }
            
            this.$message.error(errorMessage);
            this.submitLoading = false;
          });
      });
    },
    
    // 数据验证
    validateSettlementData() {
      // 检查是否选择了发货记录
      if (this.selectedShipments.length === 0) {
        this.$message.error('请选择至少一个发货记录');
        return false;
      }
      
      // 检查是否选择了产品
      if (this.selectedProductsCount === 0) {
        this.$message.error('请选择至少一个产品进行结算');
        return false;
      }
      
      // 检查结算金额是否大于等于0
      if (this.settlementTotalAmount < 0) {
        this.$message.error('结算金额必须大于等于0');
        return false;
      }
      
      // 检查每个选中的产品是否有有效的结算数量
      let hasInvalidQuantity = false;
      this.selectedShipments.forEach(shipment => {
        if (shipment.products) {
          shipment.products.forEach(item => {
            if (item.selected) {
              if (item.settlement_quantity == null || item.settlement_quantity < 0) {
                hasInvalidQuantity = true;
              }
              const maxQuantity = Number(item.shippedQuantity || item.shipped_quantity);
              if (item.settlement_quantity > maxQuantity) {
                hasInvalidQuantity = true;
              }
            }
          });
        }
      });
      
      if (hasInvalidQuantity) {
        this.$message.error('请检查产品的结算数量，确保数量大于等于0且不超过发货数量');
        return false;
      }
      
      // 检查物流信息的需求方ID是否是当前登录单位
      // 发货记录是和物流信息绑定的，不需要再确认目标单位ID
      
      return true;
    },
    
    
    // 获取需求方单位ID列表
    getDemandUnitIds() {
      // 从选中的订单中收集所有需求方单位ID
      const demandUnitIdsSet = new Set();
      
      this.selectedOrders.forEach(order => {
        if (order.contractDetails && order.contractDetails.length > 0) {
          order.contractDetails.forEach(contract => {
            if (contract.demandUnits && contract.demandUnits.length > 0) {
              contract.demandUnits.forEach(unit => {
                demandUnitIdsSet.add(unit.unitId || unit.id);
              });
            }
          });
        } else {
          // 兼容旧数据结构
          if (order.create_id) {
            demandUnitIdsSet.add(order.create_id);
          } else if (order.buyer_id) {
            demandUnitIdsSet.add(order.buyer_id);
          } else {
            // 如果都没有，使用当前用户单位ID
            demandUnitIdsSet.add(this.currentUnitId);
          }
        }
      });
      
      return Array.from(demandUnitIdsSet).filter(id => id != null);
    },
    
    // 获取需求方信息（订单的需求方作为结算对象）
    getDemandInfo() {
      // 从第一个发货记录获取需求方信息，需求方是结算的接收方
      const firstShipment = this.selectedShipments[0];
      if (!firstShipment) return { id: null, unitName: '未知需求方' };
      
      // 从发货记录关联的订单中获取需求方信息
      const orderId = firstShipment.orderId || firstShipment.order_id;
      const order = this.selectedOrders.find(o => (o.id || o.orderId) === orderId);
      
      if (order) {
        // 优先从合同详情中获取需求方信息
        if (order.contractDetails && order.contractDetails.length > 0) {
          for (const contract of order.contractDetails) {
            if (contract.demandUnits && contract.demandUnits.length > 0) {
              const demandUnit = contract.demandUnits[0];
              return {
                id: demandUnit.unitId || demandUnit.id,
                unitName: demandUnit.unitName || demandUnit.name || '未知需求方'
              };
            }
          }
        }
        
        // 兼容旧数据结构
        return {
          id: order.create_id || order.buyer_id || order.createId || order.buyerId,
          unitName: order.demand_name || order.demandName || order.demandDetails?.unitName || '未知需求方'
        };
      }
      
      return { id: null, unitName: '未知需求方' };
    },
    
    // 从发货记录中提取订单ID列表
    getOrderIdsFromShipments() {
      const orderIds = new Set();
      this.selectedShipments.forEach(shipment => {
        const orderId = shipment.orderId || shipment.order_id;
        if (orderId) {
          orderIds.add(orderId);
        }
      });
      return Array.from(orderIds);
    },
    
    // 构建结算项目列表
    buildSettlementItems() {
      const items = [];
      this.selectedShipments.forEach(shipment => {
        const products = shipment.products;
        if (products) {
          products
            .filter(item => item.selected && item.settlement_quantity > 0)
            .forEach(item => {
              const settlementAmount = this.calculateProductSettlementAmount(item);
              const taxRate = (item.taxRate || 13.00) / 100; // 转换为小数
              const taxAmount = settlementAmount * taxRate;
              const totalAmount = settlementAmount + taxAmount;
              
              items.push({
                logisticsInfoId: shipment.id,
                shipmentListId: item.id,
                orderId: shipment.orderId || shipment.order_id,
                orderNumber: shipment.orderNumber || shipment.order_number,
                productName: item.productName || item.product_name || '',
                specification: item.specification || item.spec || '',
                materialRequirement: item.materialRequirement || item.material_requirement || '',
                settledQuantity: Number(item.settlement_quantity || 0),
                unitPrice: Number(item.taxInclusiveUnitPrice || item.tax_inclusive_unit_price || 0),
                settledAmount: Number(settlementAmount.toFixed(2)),
                taxRate: Number(taxRate.toFixed(4)),
                taxAmount: Number(taxAmount.toFixed(2)),
                totalAmount: Number(totalAmount.toFixed(2)),
                remark: item.remark || ''
              });
            });
        }
      });
      return items;
    },
    
    // 返回
    goBack() {
      this.$router.push('/settlement/settlements-list');
    },
    
    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return '-';
      
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return dateString || '-';
        }
        
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      } catch (e) {
        console.error('日期格式化错误:', e);
        return dateString || '-';
      }
    },
    
    // 格式化金额
    formatAmount(amount) {
      if (amount === undefined || amount === null) return '0.00';
      return Number(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    
    // 计算订单产品总数
    calculateOrderProductsTotal(order) {
      if (!order) return 0;
      
      const orderItems = order.orderItems || order.OrderItems || [];
      if (!Array.isArray(orderItems) || orderItems.length === 0) {
        return 0;
      }
      
      // 计算所有orderItems的quantity总和
      return orderItems.reduce((total, item) => {
        const quantity = Number(item.quantity) || 0;
        return total + quantity;
      }, 0);
    }
  }
};
</script>

<style lang="scss" scoped>
.create-settlement-container {
  padding: 20px;
  background-color: #fff;
  min-height: calc(100vh - 84px);
  
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    
    .header-title {
      display: flex;
      align-items: center;
      
      .title-mark {
        width: 4px;
        height: 18px;
        background-color: #2c73eb;
        margin-right: 8px;
      }
      
      span {
        font-size: 18px;
        font-weight: 500;
        color: #303133;
      }
    }
  }
  
  .steps-container {
    margin-bottom: 30px;
    padding: 20px;
    background-color: #f5f7fa;
    border-radius: 4px;
  }
  
  .step-content {
    .section-title {
      margin-bottom: 20px;
      
      h3 {
        margin: 0 0 8px 0;
        font-size: 16px;
        font-weight: 500;
        color: #303133;
      }
      
      p {
        margin: 0;
        font-size: 14px;
        color: #606266;
      }
    }
    
    .search-area {
      margin-bottom: 20px;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      
      .search-input {
        width: 200px;
        margin-right: 15px;
        margin-bottom: 10px;
      }
      
      .el-button {
        margin-bottom: 10px;
        margin-right: 10px;
      }
    }
    
    .selected-orders-summary {
      margin-top: 20px;
      padding: 15px;
      background-color: #f0f9ff;
      border: 1px solid #b3d8ff;
      border-radius: 4px;
      
      .summary-info {
        display: flex;
        justify-content: space-between;
        align-items: center;
        
        span {
          font-size: 14px;
          color: #303133;
          
          &:last-child {
            font-weight: 500;
            color: #2c73eb;
          }
        }
      }
    }
    
    .selected-orders-info,
    .selected-shipments-info {
      margin-bottom: 20px;
      padding: 15px;
      background-color: #f5f7fa;
      border-radius: 4px;
      
      .info-title {
        font-size: 14px;
        font-weight: 500;
        color: #303133;
        margin-bottom: 10px;
      }
      
      .orders-tags,
      .shipments-tags {
        .order-tag,
        .shipment-tag {
          margin-right: 8px;
          margin-bottom: 5px;
        }
      }
    }
    
    .products-section {
      .order-products,
      .shipment-products {
        margin-bottom: 30px;
        border: 1px solid #dcdfe6;
        border-radius: 4px;
        
        .order-header,
        .shipment-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px;
          background-color: #f5f7fa;
          border-bottom: 1px solid #dcdfe6;
          
          h4 {
            margin: 0;
            font-size: 14px;
            font-weight: 500;
            color: #303133;
          }
        }
        
        .el-table {
          border: none;
        }
      }
    }
    
    .settlement-summary {
      margin-top: 20px;
      
      .summary-card {
        padding: 20px;
        background-color: #f0f9ff;
        border: 1px solid #b3d8ff;
        border-radius: 4px;
        
        .summary-title {
          font-size: 16px;
          font-weight: 500;
          color: #303133;
          margin-bottom: 15px;
        }
        
        .summary-content {
          .summary-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 10px;
            
            &:last-child {
              margin-bottom: 0;
            }
            
            span {
              font-size: 14px;
              color: #303133;
              
              &.amount {
                font-size: 16px;
                font-weight: 500;
                color: #2c73eb;
              }
              
              &.highlight {
                font-size: 18px;
                font-weight: bold;
                color: #e6a23c;
              }
            }
          }
        }
      }
    }
    
    .confirm-details {
      margin-bottom: 20px;
      
      .details-title {
        font-size: 16px;
        font-weight: 500;
        color: #303133;
        margin-bottom: 15px;
      }
      
      .orders-summary,
      .shipments-summary {
        margin-bottom: 20px;
        
        h4 {
          margin: 0 0 10px 0;
          font-size: 14px;
          font-weight: 500;
          color: #303133;
        }
      }
      
      .products-detail {
        margin-bottom: 20px;
        
        h4 {
          margin: 0 0 10px 0;
          font-size: 14px;
          font-weight: 500;
          color: #303133;
        }
      }
      
      .total-info {
        padding: 15px;
        background-color: #f5f7fa;
        border-radius: 4px;
        
        .total-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
          
          &:last-child {
            margin-bottom: 0;
          }
          
          &.highlight {
            font-size: 16px;
            font-weight: 500;
            color: #2c73eb;
          }
          
          span {
            font-size: 14px;
            color: #303133;
          }
        }
      }
    }
    
    .step-actions {
      margin-top: 30px;
      text-align: center;
      
      .el-button {
        margin: 0 10px;
      }
    }
    
    // 发货记录卡片样式
    .shipment-cards-container {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 16px;
      margin-bottom: 20px;
      
      .shipment-card {
        border: 1px solid #dcdfe6;
        border-radius: 8px;
        padding: 16px;
        cursor: pointer;
        transition: all 0.3s ease;
        background-color: #fff;
        height: fit-content;
        
        &:hover {
          border-color: #409eff;
          box-shadow: 0 2px 8px rgba(64, 158, 255, 0.2);
        }
        
        &.selected {
          border-color: #409eff;
          background-color: #f0f9ff;
          box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
        }
        
        .card-header {
          display: flex;
          flex-direction: column;
          margin-bottom: 12px;
          
          .el-checkbox {
            margin-bottom: 8px;
            align-self: flex-start;
          }
          
          .tracking-number {
            font-size: 16px;
            font-weight: 500;
            color: #303133;
            margin-bottom: 8px;
            word-break: break-all;
          }
          
          .logistics-company {
            font-size: 12px;
            color: #606266;
            background-color: #f5f7fa;
            padding: 4px 8px;
            border-radius: 4px;
            display: inline-block;
            width: fit-content;
          }
        }
        
        .card-content {
          .info-row {
            display: flex;
            margin-bottom: 6px;
            
            &:last-child {
              margin-bottom: 0;
            }
            
            .label {
              width: 60px;
              font-size: 12px;
              color: #909399;
              flex-shrink: 0;
            }
            
            .value {
              font-size: 12px;
              color: #303133;
              flex: 1;
              word-break: break-all;
            }
          }
        }
      }
      
      .select-all-container {
        grid-column: 1 / -1;
        text-align: center;
        padding: 16px;
        background-color: #f5f7fa;
        border-radius: 8px;
      }
    }
    
    // 响应式布局
    @media (max-width: 1200px) {
      .shipment-cards-container {
        grid-template-columns: repeat(2, 1fr);
      }
    }
    
    @media (max-width: 768px) {
      .shipment-cards-container {
        grid-template-columns: 1fr;
      }
    }
  }
}
</style>

<style lang="scss">
.spec-tooltip {
  max-width: 600px !important;
  white-space: normal;
  word-break: break-all;
}
</style>